import { TableV3Filters } from 'common/table/components/TableV3';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import {
  baseId,
  isSelection,
  withFreeIds
} from 'modules/candidate-profile/tables/filters/candidateFilterState';
import {
  chipGroupsInOrder,
  groupsAsConditions
} from 'modules/candidate-profile/tables/filters/candidateFilterGroups';
import { isUniversalSearchColumn } from 'modules/candidate-profile/tables/filters/searchesToConditions';
import {
  CandidateFilterGroups,
  CandidateFilterMatchMode,
  CandidateFilterState
} from 'modules/candidate-profile/types/CandidateSearchFilter';

export type SearchFields =
  TableV3Filters<CandidateProfileTableColumns>['searchFields'];

export const asSearchFields = (fields: Record<string, string>) =>
  (Object.keys(fields).length ? fields : undefined) as SearchFields;

export const withoutFields = (
  conditions: CandidateFilterState[],
  fields: Set<string>
): CandidateFilterState[] =>
  conditions.filter((condition) => !fields.has(condition.field));

/**
 * Combine a condition from the search with the chip that already holds it.
 *
 * Selections add their values together. Nothing else can be combined, so one
 * wins: the search's when the chip was sent with the turn, else the chip.
 */
const combineCondition = (
  chip: CandidateFilterState,
  incoming: CandidateFilterState,
  wasSent: boolean,
  isSearchColumn: (field: string) => boolean
): CandidateFilterState => {
  if (
    !isSelection(chip) ||
    !isSelection(incoming) ||
    isSearchColumn(chip.field)
  ) {
    return wasSent ? { ...incoming, id: chip.id } : chip;
  }

  return {
    ...chip,
    values: [...new Set([...chip.values, ...incoming.values])],
    ...(chip.valueLabels || incoming.valueLabels
      ? { valueLabels: { ...incoming.valueLabels, ...chip.valueLabels } }
      : {})
  };
};

// Paired by base id, so a chip that kept an ordinal still meets its echo.
export const groupKey = baseId;

/**
 * Merge the search's conditions into the chips, one field and mode at a time.
 *
 * `sent` is the chips the request was built from, in order. The search hands
 * back what was sent, so each sent condition is paired with its returned copy. A
 * sent condition that is no longer a chip was changed while the turn was in
 * flight; its returned copy is out of date, so it is dropped and not merged
 * back.
 *
 * An incoming condition with no pair is one the assistant derived. It is a
 * second constraint AND-ed beside the first, so it becomes its own chip.
 */
export const mergeConditions = (
  chips: CandidateFilterState[],
  incoming: CandidateFilterState[],
  sent?: CandidateFilterState[],
  isSearchColumn: (field: string) => boolean = isUniversalSearchColumn
): {
  conditions: CandidateFilterState[];
  chipFor: Map<CandidateFilterState, CandidateFilterState>;
} => {
  const incomingByGroup = new Map<string, CandidateFilterState[]>();
  incoming.forEach((condition) => {
    const key = groupKey(condition);
    incomingByGroup.set(key, [...(incomingByGroup.get(key) ?? []), condition]);
  });

  const wasSent = new Set(sent ?? []);
  const returnedFor = new Map<CandidateFilterState, CandidateFilterState>();
  (sent ?? chips).forEach((condition) => {
    const returned = incomingByGroup.get(groupKey(condition))?.shift();
    if (returned) returnedFor.set(condition, returned);
  });

  // A search input holds one term, so a term typed mid-turn takes the reply's.
  chips
    .filter(
      (chip) =>
        !returnedFor.has(chip) &&
        isSelection(chip) &&
        chip.mode === CandidateFilterMatchMode.INCLUDE_ANY &&
        isSearchColumn(chip.field)
    )
    .forEach((chip) => {
      const returned = incomingByGroup.get(groupKey(chip))?.shift();
      if (returned) returnedFor.set(chip, returned);
    });

  const paired = new Set(returnedFor.values());
  const chipFor = new Map<CandidateFilterState, CandidateFilterState>();
  const merged = chips.map((chip) => {
    const returned = returnedFor.get(chip);
    if (!returned) return chip;

    const combined = combineCondition(
      chip,
      returned,
      wasSent.has(chip),
      isSearchColumn
    );
    chipFor.set(returned, combined);
    return combined;
  });

  const unpaired = incoming.filter((condition) => !paired.has(condition));
  const added = withFreeIds(unpaired, merged);
  unpaired.forEach((condition, at) => chipFor.set(condition, added[at]));

  return { conditions: [...merged, ...added], chipFor };
};

const questionKey = (condition: CandidateFilterState): string => {
  if (isSelection(condition)) {
    return `${groupKey(condition)}=${[...condition.values].sort().join('|')}`;
  }

  const keys = Object.keys(condition).filter((key) => key !== 'id');
  return JSON.stringify(condition, keys.sort());
};

const orGroupKey = (group: CandidateFilterState[]): string =>
  group.map(questionKey).sort().join(' ');

type ChipsAndGroups = {
  conditions: CandidateFilterState[];
  groups: CandidateFilterGroups;
};

// `sent` is the rail the request was built from; nothing pairs without it.
export const mergeReply = (
  rail: ChipsAndGroups,
  reply: ChipsAndGroups,
  sent?: ChipsAndGroups,
  isSearchColumn: (field: string) => boolean = isUniversalSearchColumn
): ChipsAndGroups => {
  const sentInRequestOrder =
    sent && chipGroupsInOrder(sent.conditions, sent.groups).flat();
  const { conditions, chipFor } = mergeConditions(
    rail.conditions,
    reply.conditions,
    sentInRequestOrder,
    isSearchColumn
  );

  // A reply repeats the groups it was sent; a split one stays split.
  const sentGroupKeys = new Set(
    groupsAsConditions(sent?.conditions ?? [], sent?.groups ?? []).map(
      orGroupKey
    )
  );
  const replyGroups = groupsAsConditions(reply.conditions, reply.groups)
    .filter((group) => !sentGroupKeys.has(orGroupKey(group)))
    .map((group) => group.map((condition) => chipFor.get(condition)?.id))
    .filter((group): group is string[] => group.every(Boolean));

  // A chip is in one Or group only. A new group from the reply wins.
  const inReplyGroup = new Set(replyGroups.flat());
  const railGroups = rail.groups.filter(
    (group) => !group.some((id) => inReplyGroup.has(id))
  );

  return { conditions, groups: [...railGroups, ...replyGroups] };
};
